<template>
  <div class="sign-page">
    <div class="panel">
      <div class="row preview-full">
        <div id="BJCAPdfDiv" class="preview-container"></div>
      </div>
      <div v-if="errorMsg" class="error-container">
        <div class="error-icon">⚠️</div>
        <div class="error-content">
          <h4>PDF签章客户端连接失败</h4>
          <pre>{{ errorMsg }}</pre>
          <div class="error-actions">
            <el-button type="primary" size="small" @click="retryConnection">重试连接</el-button>
            <el-button type="default" size="small" @click="showHelp">查看帮助</el-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

// 常量配置 - 根据当前页面协议动态选择
const isHttps = window.location.protocol === 'https:'
const PDF_CLIENT_URL = isHttps
  ? 'http://127.0.0.1:21051/PDFClient/viewer.html'  // HTTPS端口（需要客户端支持）
  : 'http://127.0.0.1:21051/PDFClient/viewer.html'   // HTTP端口
const PDF_CLIENT_ORIGIN = isHttps
  ? 'http://127.0.0.1:21051'
  : 'http://127.0.0.1:21051'
const IFRAME_ID = 'BJCAPdfJsViewer'// iframe ID
const CONTAINER_ID = 'BJCAPdfDiv'// 容器ID
// 消息类型常量
const MESSAGE_TYPES = {
  INIT_SUCCESS: 'initSucceeded',// 初始化成功
  SIGN_PDF: 'signPdfBase64',// 签名PDF
  LOCAL_SIGN_SUCCESS: 'localSignSucceeded',// 本地签名成功
  FILE_LOAD_FAILED: 'fileLoadFailed',// 文件加载失败
  FILE_NOT_FOUND: 'fileNotFound',// 文件未找到
  ERROR: 'error',// 错误
  INIT_FAILED: 'initFailed'// 初始化失败
}
export default {
  name: 'PdfSignBridge',
  data() {
    return {
      // 配置参数
      sectionCode: '',// 标段编号
      saveType: 'data',// 保存类型
      showPagingSealBtn: 1,// 是否显示分页印章按钮
      showSignContentBtn: 1,// 是否显示签名内容按钮
      showVerifyBtn: 1,// 是否显示验证按钮
      token: Math.random(),// 令牌
      remotePdfUrl: '',// 远程PDF地址
      pdfBase64: '',// base64 PDF 内容（接口返回）
      errorMsg: ''// 错误信息
    }
  },
  mounted() {
    window.addEventListener('message', this.handleMessage, false)
    this.initPage()
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleMessage, false) // 移除消息监听
  },
  methods: {
    // 初始化页面
    initPage() {
      const sectionCode = this.$route.query.sectionCode
      this.sectionCode = sectionCode
      if (!sectionCode) {
        this.$message.error('缺少标段编号参数')
        this.$router.go(-1)
        return
      }
      // 优先从 sessionStorage 读取缓存的 PDF base64
      const source = this.$route.query.source
      if (source === 'cache') {
        const cachedBase64 = sessionStorage.getItem('pdfSignBase64')
        if (cachedBase64 && cachedBase64.trim()) {
          this.pdfBase64 = cachedBase64
          this.remotePdfUrl = ''
          sessionStorage.removeItem('pdfSignBase64')
          this.initViewer()
          return
        }
      }
    },
    // 初始化PDF查看器
    async initViewer() {
      this.errorMsg = ''
      const dom = document.getElementById(CONTAINER_ID)
      if (!dom) return

      // 先检测PDF客户端是否可用
      const isClientAvailable = await this.checkPdfClient()
      if (!isClientAvailable) {
        this.errorMsg = `PDF签章客户端连接失败，请确认：
1. 已安装并启动PDF签章客户端
2. 客户端运行在端口 ${isHttps ? '21052' : '21051'}
3. 如使用HTTPS，请确保客户端支持HTTPS协议`
        return
      }

      const isDataMode = !!(this.pdfBase64 && this.pdfBase64.trim())
      const effectivePath = (this.remotePdfUrl && this.remotePdfUrl.trim())
      if (!isDataMode && !effectivePath) {
        this.errorMsg = '未找到可用的 PDF 地址'
        this.$message.warning('未找到可用的 PDF 地址')
        return
      }
      const queryParams = this.buildQueryParams(isDataMode ? '' : effectivePath)
      const src = `${PDF_CLIENT_URL}?${queryParams}`
      this.logInitInfo(isDataMode ? '[data-mode]' : effectivePath, src)
      dom.innerHTML = `<iframe src="${src}" id="${IFRAME_ID}" frameborder="0" scrolling="auto" width="100%" height="100%"></iframe>`
    },
    // 构建查询参数
    buildQueryParams(filePathOrUrl) {
      const isDataMode = !!(this.pdfBase64 && this.pdfBase64.trim())
      const params = {
        openType: isDataMode ? 'data' : 'url',
        saveType: this.saveType || 'data',
        token: this.token || Math.random(),
        showPagingSealBtn: this.showPagingSealBtn ?? '1',
        showSignContentBtn: this.showSignContentBtn ?? '1',
        showVerifyBtn: this.showVerifyBtn ?? '1'
      }
      if (!isDataMode) {
        params.fileUrl = encodeURIComponent(filePathOrUrl)
      }
      return Object.entries(params)
        .map(([key, value]) => {
          const encodedValue = key === 'fileUrl' ? value : encodeURIComponent(value)
          return `${key}=${encodedValue}`
        })
        .join('&')
    },
    // 检测PDF客户端是否可用
    async checkPdfClient() {
      try {
        const response = await fetch(PDF_CLIENT_ORIGIN, {
          method: 'GET',
          mode: 'no-cors',
          timeout: 3000
        })
        return true
      } catch (error) {
        console.error('PDF客户端连接检测失败:', error)
        return false
      }
    },
    // 重试连接
    retryConnection() {
      this.errorMsg = ''
      this.initViewer()
    },
    // 显示帮助信息
    showHelp() {
      this.$alert(`
PDF签章客户端连接问题解决方案：

1. 确认客户端已安装并启动
   - 检查系统托盘是否有PDF签章客户端图标
   - 如未启动，请手动启动客户端程序

2. 检查端口配置
   - HTTP环境：客户端应运行在 127.0.0.1:21051
   - HTTPS环境：客户端应运行在 127.0.0.1:21052

3. 防火墙设置
   - 确保防火墙允许客户端端口通信
   - 临时关闭防火墙测试连接

4. 协议匹配
   - 当前页面协议：${window.location.protocol}
   - 客户端地址：${PDF_CLIENT_ORIGIN}

5. 联系技术支持
   - 如问题持续，请联系PDF签章客户端技术支持
      `, 'PDF签章客户端帮助', {
        confirmButtonText: '确定',
        type: 'info'
      })
    },
    // 记录初始化信息
    logInitInfo(filePath, src) {
      console.log('文件路径:', filePath)
      console.log('pdf路径', src)
    },
    // 处理消息
    handleMessage(e) {
      const message = e.data
      if (!message || typeof message !== 'object') return
      // 验证消息来源
      if (e.origin && e.origin !== PDF_CLIENT_ORIGIN && !e.origin.includes('127.0.0.1')) {
        console.warn('收到来自未知源的消息:', e.origin, message)
      }
      const handlers = {
        [MESSAGE_TYPES.INIT_SUCCESS]: this.handleInitSuccess,
        [MESSAGE_TYPES.SIGN_PDF]: this.handleSignPdf,
        [MESSAGE_TYPES.LOCAL_SIGN_SUCCESS]: this.handleLocalSignSuccess,
        [MESSAGE_TYPES.FILE_LOAD_FAILED]: this.handleFileLoadError,
        [MESSAGE_TYPES.FILE_NOT_FOUND]: this.handleFileLoadError,
        [MESSAGE_TYPES.ERROR]: this.handleError,
        [MESSAGE_TYPES.INIT_FAILED]: this.handleError
      }

      const handler = handlers[message.type]
      if (handler) {
        handler.call(this, message)
      } else {
        console.log('收到其他消息类型:', message.type, message)
      }
    },
    // 处理初始化成功
    handleInitSuccess() {
      console.log('PDF 客户端初始化成功')
      this.errorMsg = ''
      // 如果是 base64 模式，在初始化完成后将 base64 传给客户端
      if (this.pdfBase64 && this.pdfBase64.trim()) {
        try {
          const iframe = document.getElementById(IFRAME_ID)
          if (iframe && iframe.contentWindow) {
            const payload = {
              type: 'fileBase64',
              fileType: 'pdf',
              token: this.token,
              contents: this.pdfBase64
            }
            iframe.contentWindow.postMessage(payload, PDF_CLIENT_ORIGIN)
          }
        } catch (e) {
          console.error('发送 base64 到 PDF 客户端失败', e)
        }
      }
    },
    // 处理签后
    handleSignPdf(message) {
      const base64 = message.contents || ''
      if (!base64) {
        console.error('未获取到签后 Base64')
      } else {
        this.submitSign(base64)
      }
    },

    // 提交签名
    async submitSign(base64) {

      sessionStorage.setItem('pdfSignedBase64', base64)
      setTimeout(() => {
        this.$router.go(-1)
      }, 1000);
    },
    // 处理本地签名成功
    handleLocalSignSuccess() {
    },

    // 处理文件加载错误
    handleFileLoadError(message) {
      const errMsg = message.message || message.error || '文件加载失败，请检查文件路径是否正确'
      this.errorMsg = errMsg
      this.$message.error(`文件加载失败：${errMsg}`)
    },
    // 处理错误
    handleError(message) {
      const errMsg = message.message || message.error || '未知错误'
      this.errorMsg = errMsg
      console.error('PDF 客户端错误:', errMsg, message)
      this.$message.error(`PDF 客户端错误：${errMsg}`)
    },

  }
}
</script>

<style scoped>
.sign-page {
  padding: 0;
  height: 100vh;
}

.panel {
  margin: 0;
  padding: 0;
  max-width: 100%;
  height: 100vh;
  border: none;
  border-radius: 0;
  box-shadow: none;
  background: #fff;
}

.row {
  margin: 0;
}

.preview-full {
  height: 100vh;
}

.preview-container {
  height: 100%;
}

label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
}

.tips {
  color: #909399;
  font-size: 12px;
  margin-top: 6px;
}

.error {
  margin-top: 12px;
  color: #f56c6c;
}

/* 错误提示样式 */
.error-container {
  display: flex;
  align-items: flex-start;
  padding: 20px;
  margin: 20px;
  background-color: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 6px;
  color: #ff4d4f;
}

.error-icon {
  font-size: 24px;
  margin-right: 12px;
  flex-shrink: 0;
}

.error-content {
  flex: 1;
}

.error-content h4 {
  margin: 0 0 10px 0;
  font-size: 16px;
  font-weight: 600;
}

.error-content pre {
  margin: 10px 0;
  padding: 10px;
  background-color: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.error-actions {
  margin-top: 15px;
}

.error-actions .el-button {
  margin-right: 10px;
}
</style>